/**
 * Phase 3F: Governance Admin Page
 * Manages feature flags, client opt-ins, and audit logs
 */

import React, { useState, useEffect } from 'react';
import { Phase3AdminPanel, Phase3Disclaimer } from '@/components/governance';
import { OpsLayout } from '@/components/ops/OpsLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePhase3Governance } from '@/hooks/usePhase3Governance';
import { useClients } from '@/hooks/useClientSettings';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import {
  Shield,
  History,
  Users,
  RefreshCw,
  Search,
  Download,
} from 'lucide-react';
import type { Phase3AuditEvent, Phase3AuditEventType } from '@/lib/governance/types';

export default function Phase3GovernancePage() {
  const { user, role } = useAuth();
  const { clients, isLoading: clientsLoading } = useClients();
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('controls');
  
  const selectedClient = clients.find(c => c.id === selectedClientId);
  
  const {
    featureFlags,
    clientOptIn,
    isLoading: governanceLoading,
    toggleFeatureFlag,
    setClientOptIn,
  } = usePhase3Governance(
    user?.id || null,
    selectedClientId,
    role || null
  );

  useEffect(() => {
    if (clients.length > 0 && !selectedClientId) {
      setSelectedClientId(clients[0].id);
    }
  }, [clients, selectedClientId]);

  return (
    <OpsLayout>
      <div className="w-full max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary" />
              Phase 3 Governance
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage feature flags, opt-ins, and audit trails for Phase 3 analytics
            </p>
          </div>
          
          <Select
            value={selectedClientId || ''}
            onValueChange={setSelectedClientId}
          >
            <SelectTrigger className="w-[250px]">
              <SelectValue placeholder="Select a hospital" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.client_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Phase3Disclaimer variant="banner" />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="controls">
              <Shield className="h-4 w-4 mr-1" />
              Controls
            </TabsTrigger>
            <TabsTrigger value="surgeons">
              <Users className="h-4 w-4 mr-1" />
              Surgeon Opt-Ins
            </TabsTrigger>
            <TabsTrigger value="audit">
              <History className="h-4 w-4 mr-1" />
              Audit Log
            </TabsTrigger>
          </TabsList>

          <TabsContent value="controls" className="mt-6">
            <Phase3AdminPanel
              featureFlags={featureFlags}
              clientOptIn={clientOptIn}
              onToggleFeatureFlag={toggleFeatureFlag}
              onToggleClientOptIn={setClientOptIn}
              isLoading={governanceLoading || clientsLoading}
              clientName={selectedClient?.client_name}
            />
          </TabsContent>

          <TabsContent value="surgeons" className="mt-6">
            <SurgeonOptInsTable clientId={selectedClientId} />
          </TabsContent>

          <TabsContent value="audit" className="mt-6">
            <AuditLogTable clientId={selectedClientId} />
          </TabsContent>
        </Tabs>
      </div>
    </OpsLayout>
  );
}

function SurgeonOptInsTable({ clientId }: { clientId: string | null }) {
  const [optIns, setOptIns] = useState<Array<{
    id: string;
    surgeonId: string;
    optedIn: boolean;
    optedInAt?: string;
    disclaimerAccepted: boolean;
    disclaimerAcceptedAt?: string;
  }>>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    
    setIsLoading(true);
    supabase
      .from('phase3_surgeon_optins')
      .select('id, surgeon_id, opted_in, opted_in_at, disclaimer_accepted, disclaimer_accepted_at')
      .eq('client_id', clientId)
      .then(({ data, error }) => {
        if (!error && data) {
          setOptIns(data.map(d => ({
            id: d.id,
            surgeonId: d.surgeon_id,
            optedIn: d.opted_in,
            optedInAt: d.opted_in_at || undefined,
            disclaimerAccepted: d.disclaimer_accepted,
            disclaimerAcceptedAt: d.disclaimer_accepted_at || undefined,
          })));
        }
        setIsLoading(false);
      });
  }, [clientId]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <RefreshCw className="h-6 w-6 mx-auto mb-2 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground">Loading surgeon opt-ins...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Surgeon Opt-In Status</CardTitle>
        <CardDescription>Individual surgeon opt-in status for this hospital</CardDescription>
      </CardHeader>
      <CardContent>
        {optIns.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No surgeon opt-ins recorded yet</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Surgeon ID</TableHead>
                <TableHead>Opted In</TableHead>
                <TableHead>Opted In Date</TableHead>
                <TableHead>Disclaimer</TableHead>
                <TableHead>Disclaimer Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {optIns.map((optIn) => (
                <TableRow key={optIn.id}>
                  <TableCell className="font-mono text-xs">
                    {optIn.surgeonId.slice(0, 8)}...
                  </TableCell>
                  <TableCell>
                    <Badge variant={optIn.optedIn ? 'default' : 'outline'}>
                      {optIn.optedIn ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {optIn.optedInAt ? format(new Date(optIn.optedInAt), 'MMM d, yyyy HH:mm') : '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={optIn.disclaimerAccepted ? 'default' : 'outline'}>
                      {optIn.disclaimerAccepted ? 'Accepted' : 'Pending'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {optIn.disclaimerAcceptedAt ? format(new Date(optIn.disclaimerAcceptedAt), 'MMM d, yyyy HH:mm') : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function AuditLogTable({ clientId }: { clientId: string | null }) {
  const [logs, setLogs] = useState<Phase3AuditEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState<Phase3AuditEventType | 'all'>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    setIsLoading(true);
    
    let query = supabase
      .from('phase3_audit_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100);
    
    if (clientId) {
      query = query.eq('client_id', clientId);
    }
    
    if (filter !== 'all') {
      query = query.eq('event_type', filter);
    }
    
    query.then(({ data, error }) => {
      if (!error && data) {
        setLogs(data.map(d => ({
          id: d.id,
          eventType: d.event_type as Phase3AuditEventType,
          actorId: d.actor_id,
          actorRole: d.actor_role || undefined,
          clientId: d.client_id || undefined,
          surgeonId: d.surgeon_id || undefined,
          insightType: d.insight_type || undefined,
          insightId: d.insight_id || undefined,
          featureName: d.feature_name || undefined,
          previousValue: d.previous_value as Record<string, unknown> || undefined,
          newValue: d.new_value as Record<string, unknown> || undefined,
          metadata: d.metadata as Record<string, unknown> || undefined,
          ipAddress: d.ip_address || undefined,
          userAgent: d.user_agent || undefined,
          createdAt: d.created_at,
        })));
      }
      setIsLoading(false);
    });
  }, [clientId, filter]);

  const filteredLogs = logs.filter(log => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      log.eventType.toLowerCase().includes(searchLower) ||
      log.actorId.toLowerCase().includes(searchLower) ||
      (log.featureName && log.featureName.toLowerCase().includes(searchLower)) ||
      (log.insightType && log.insightType.toLowerCase().includes(searchLower))
    );
  });

  const getEventBadgeVariant = (eventType: Phase3AuditEventType) => {
    if (eventType.includes('opted_in') || eventType === 'disclaimer_accepted') {
      return 'default';
    }
    if (eventType.includes('opted_out') || eventType === 'insight_dismissed') {
      return 'secondary';
    }
    return 'outline';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Audit Log</CardTitle>
            <CardDescription>Complete audit trail for Phase 3 governance events</CardDescription>
          </div>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-1" />
            Export
          </Button>
        </div>
        
        <div className="flex items-center gap-3 mt-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search logs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            value={filter}
            onValueChange={(v) => setFilter(v as Phase3AuditEventType | 'all')}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              <SelectItem value="insight_shown">Insights Shown</SelectItem>
              <SelectItem value="insight_dismissed">Insights Dismissed</SelectItem>
              <SelectItem value="insight_override">Insights Overridden</SelectItem>
              <SelectItem value="feature_flag_changed">Flag Changes</SelectItem>
              <SelectItem value="client_opted_in">Client Opt-Ins</SelectItem>
              <SelectItem value="surgeon_opted_in">Surgeon Opt-Ins</SelectItem>
              <SelectItem value="disclaimer_accepted">Disclaimers Accepted</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8">
            <RefreshCw className="h-6 w-6 mx-auto mb-2 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Loading audit logs...</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No audit logs found</p>
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Event Type</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm whitespace-nowrap">
                      {format(new Date(log.createdAt), 'MMM d, HH:mm:ss')}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getEventBadgeVariant(log.eventType)}>
                        {log.eventType.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {log.actorId.slice(0, 8)}...
                      {log.actorRole && (
                        <span className="text-muted-foreground ml-1">({log.actorRole})</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {log.featureName || log.insightType || log.insightId || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
